import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Paperclip, X, Loader2, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('adminToken');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const absolute = (url) => (url?.startsWith('http') ? url : `${BACKEND_URL}${url}`);

/**
 * Photo/file attachments for a work order.
 * Camera button opens the phone camera directly; files upload with a progress bar.
 */
export const WorkOrderAttachments = ({ attachments = [], onChange, readOnly = false }) => {
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(null); // { name, percent }

  const upload = async (fileList, source) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const added = [];
    for (const file of files) {
      if (file.size > 15 * 1024 * 1024) {
        toast.error(`${file.name} is over 15MB`);
        continue;
      }
      setUploading({ name: file.name, percent: 0 });
      try {
        const form = new FormData();
        form.append('file', file);
        const { data } = await authAxios.post(`${API}/work-orders/attachments?source=${source}`, form, {
          onUploadProgress: (e) => {
            if (e.total) setUploading({ name: file.name, percent: Math.round((e.loaded / e.total) * 100) });
          },
        });
        added.push(data);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    setUploading(null);
    if (added.length) {
      onChange?.([...attachments, ...added]);
      toast.success(`${added.length} ${added.length > 1 ? 'files' : 'file'} attached`);
    }
  };

  const remove = async (id) => {
    const next = attachments.filter((a) => a.id !== id);
    onChange?.(next);
    try {
      await authAxios.delete(`${API}/work-orders/attachments/${id}`);
    } catch {
      /* soft-delete failure is non-fatal; the record is already detached */
    }
  };

  return (
    <div data-testid="wo-attachments">
      {attachments.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
          {attachments.map((a) => (
            <div key={a.id} className="relative group" data-testid={`wo-attachment-${a.id}`}>
              {String(a.content_type || '').startsWith('image/') ? (
                <a href={absolute(a.url)} target="_blank" rel="noreferrer">
                  <img
                    src={absolute(a.url)}
                    alt={a.filename}
                    className="w-full aspect-square object-cover rounded-lg border border-white/10"
                  />
                </a>
              ) : (
                <a
                  href={absolute(a.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full aspect-square rounded-lg border border-white/10 bg-white/5 flex flex-col items-center justify-center p-2 text-center"
                >
                  <FileText className="w-6 h-6 text-slate-400" />
                  <span className="text-[10px] text-slate-400 mt-1 truncate w-full">{a.filename}</span>
                </a>
              )}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => remove(a.id)}
                  className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg"
                  aria-label={`Remove ${a.filename}`}
                  data-testid={`wo-attachment-remove-${a.id}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {uploading && (
        <div className="mb-3" data-testid="wo-attachment-progress">
          <div className="flex items-center gap-2 text-xs text-slate-300 mb-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span className="truncate flex-1">{uploading.name}</span>
            <span className="tabular-nums">{uploading.percent}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-cyan-400 transition-all" style={{ width: `${uploading.percent}%` }} />
          </div>
        </div>
      )}

      {!readOnly && (
        <>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              className="flex-1 min-h-[48px] rounded-xl bg-cyan-500/15 border border-cyan-400/30 text-cyan-100 font-medium flex items-center justify-center gap-2"
              data-testid="wo-attachment-camera-btn"
            >
              <Camera className="w-4 h-4" /> Take Photo
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex-1 min-h-[48px] rounded-xl bg-white/5 border border-white/15 text-slate-200 font-medium flex items-center justify-center gap-2"
              data-testid="wo-attachment-file-btn"
            >
              <Paperclip className="w-4 h-4" /> Attach File
            </button>
          </div>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => { upload(e.target.files, 'camera'); e.target.value = ''; }}
            data-testid="wo-attachment-camera-input"
          />
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*,application/pdf,.csv,.txt"
            className="hidden"
            onChange={(e) => { upload(e.target.files, 'file'); e.target.value = ''; }}
            data-testid="wo-attachment-file-input"
          />
        </>
      )}
    </div>
  );
};

export default WorkOrderAttachments;
